import { FormField, FormError } from './utils';
import { CONSTANTS } from './constants';
import { checkRelatedRadios } from '../utilities/input';
import { checkCheckboxGroup } from '../utilities/input';
import * as vinValidator from '../utilities/vin';

export function validateInputLength(f: FormField, validationType: string): FormError {
  let field = f.field as HTMLInputElement;
  const formError = {
    type: validationType,
    message: f.message || 'The value does not match the length required.',
  };

  // When minLength or maxLength is not defined, it's set as -1
  const hasMinLength = field.minLength > 0;
  const hasMaxLength = field.maxLength > 0;
  const valueLength = field.value.length;

  if (hasMinLength && valueLength < field.minLength) {
    return formError;
  }

  if (hasMaxLength && valueLength > field.maxLength) {
    return formError;
  }

  return null;
}

export function validateInputPattern(f: FormField, validationType: string): FormError {
  let field = f.field as HTMLInputElement;
  const formError = {
    type: validationType,
    message: f.message || 'The input provided does not match expectations.',
  };

  return field.validity.patternMismatch ? formError : null;
}

export function validateEmail(f: FormField, validationType: string): FormError {
  let field = f.field as HTMLInputElement;
  const pattern = RegExp(CONSTANTS.emailPattern);

  const formError = {
    type: validationType,
    message: f.message || 'Please enter a valid e-mail address.',
  };

  if (field.value !== '') {
    // We use more than just field.validity due to our more restrictive email requirements.
    return field.validity.valid && pattern.test(field.value) ? null : formError;
  } else {
    return null;
  }
}

export function validateVin(f: FormField, validationType: string): FormError {
  const invalidVinFormat = {
    type: validationType,
    message: f.message || 'Please enter a valid VIN.',
  };
  const invalidVinCheckDigit = {
    type: validationType,
    message: f.message || 'VIN check digit is invalid.',
  };
  const pattern = RegExp(CONSTANTS.vinPattern);

  if (f.field.value == '') {
    return null;
  }

  if (!pattern.test(f.field.value)) {
    return invalidVinFormat;
  }

  let expectedCheckDigit = vinValidator.calculated_check_digit(f.field.value);
  if (f.field.value[8].toUpperCase() == 'X')
    return expectedCheckDigit == 10 ? null : invalidVinCheckDigit;
  else return expectedCheckDigit == parseInt(f.field.value[8]) ? null : invalidVinCheckDigit;
}

export function validateRequiredField(f: FormField, validationType: string): FormError {
  const formError = { type: validationType, message: f.message || 'This field is required.' };

  if (
    (f.field instanceof HTMLInputElement || f.field instanceof HTMLTextAreaElement) &&
    f.field.dataset.component !== 'multiple-autocomplete'
  ) {
    return f.field.value.length === 0 ? formError : null;
  } else if (f.field instanceof HTMLSelectElement) {
    if (f.field.multiple) {
      return f.field.selectedOptions.length === 0 ? formError : null;
    } else {
      return f.field.value.length === 0 ? formError : null;
    }
  } else {
    return null;
  }
}

export function validateRadioButton(f: FormField, validationType: string): FormError {
  const formError = { type: validationType, message: f.message || 'This field is required.' };

  let notChecked = checkRelatedRadios(f.field.form, f.field as HTMLInputElement);

  return notChecked ? null : formError;
}

export function validateCheckboxGroup(f: HTMLFormElement, validationType: string): FormError {
  const formError = {
    type: validationType,
    message: f.message || 'At least one checkbox must be selected',
  };

  return checkCheckboxGroup(f).length > 0 ? null : formError;
}

export function validateMinMax(f: FormField, validationType: string): FormError {
  let field = f.field as HTMLInputElement;
  let min = field.min || null;
  let max = field.max || null;

  if (max !== null && Number.parseFloat(field.value) > Number.parseFloat(max)) {
    return { type: validationType, message: 'The input is higher than the maximum allows.' };
  } else if (min !== null && Number.parseFloat(field.value) < Number.parseFloat(min)) {
    return { type: validationType, message: 'The input is lower than the minimum allows.' };
  } else {
    return null;
  }
}

export function validateNumericMinValue(f: FormField, validationType: string): FormError {
  let minValue = f.field.dataset.validateNumericRangeMin;
  let value = f.field.value.replace(/[^0-9.]/g, '');

  const formError = {
    type: validationType,
    message: f.message || 'The input supplied is lower than accepted.',
  };

  return Number.parseFloat(minValue) > Number.parseFloat(value) ? formError : null;
}

export function validateNumericMaxValue(f: FormField, validationType: string): FormError {
  let maxValue = f.field.dataset.validateNumericRangeMax;
  let value = f.field.value.replace(/[^0-9.]/g, '');

  let formError = {
    type: validationType,
    message: f.message || 'The input supplied is higher than accepted.',
  };

  return Number.parseFloat(maxValue) < Number.parseFloat(value) ? formError : null;
}

export function validateTelephoneNumber(f: FormField, validationType: string): FormError {
  const formError = {
    type: validationType,
    message: f.message || 'The phone number entered is not valid.',
  };
  let phonePattern = RegExp(CONSTANTS.phonePattern);

  return phonePattern.test(f.field.value) ? null : formError;
}

// Custom Luhn algorithm implementation for credit card validation
export function luhnCheck(cardNumber: string): boolean {
  // Remove any non-digit characters
  const digits = cardNumber.replace(/\D/g, '');

  // Must be at least 13 digits and at most 19 digits
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  let sum = 0;
  let alternate = false;

  // Process digits from right to left
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits.charAt(i));

    if (alternate) {
      digit *= 2;
      if (digit > 9) {
        digit = (digit % 10) + 1;
      }
    }

    sum += digit;
    alternate = !alternate;
  }

  return sum % 10 === 0;
}

// Custom credit card validation using Luhn algorithm
export function validateCreditCardField(f: FormField, validationType: string): FormError | null {
  let value = f.field.value;
  const formError = {
    type: validationType,
    message: f.message || 'This is not a valid credit card.',
  };

  // Remove spaces and dashes for validation
  const cleanValue = value.replace(/[\s-]/g, '');

  // Check if empty
  if (!cleanValue) {
    return null; // Let required validation handle empty fields
  }

  // Validate using Luhn algorithm
  const isValid = luhnCheck(cleanValue);

  return isValid ? null : formError;
}

export function validateTextAsNumeric(f: FormField, validationType: string): FormError {
  let numericDigits = new RegExp(/^\d+$/);
  const formError = {
    type: validationType,
    message: f.message || 'The value must only contain numeric digits.',
  };

  return numericDigits.test(f.field.value) ? null : formError;
}

export function validateMatchingField(
  f: FormField,
  matchingField: FormField,
  validationType: string
): FormError {
  let formError = {
    type: validationType,
    message: f.message || "This does not match the associated field's input.",
  };

  return f.field.value === matchingField.field.value ? null : formError;
}
